
import React, { useState, useEffect, useRef } from 'react';
import { PopupContent } from '@/types/popup-system';
import confetti from 'canvas-confetti';

interface CosmicHorrorPopupProps {
  content: PopupContent;
  onDismiss: () => void;
  onAction?: (action: string) => void;
}

export const CosmicHorrorPopup = ({ content, onDismiss, onAction }: CosmicHorrorPopupProps) => {
  const [void_pulse, setVoidPulse] = useState(false);
  const [textShift, setTextShift] = useState(false);
  const [glowIntensity, setGlowIntensity] = useState(0.3);
  const confettiFired = useRef(false);

  // Fire confetti on mount
  useEffect(() => {
    if (confettiFired.current) return;
    confettiFired.current = true;

    const timer = setTimeout(() => {
      confetti({
        particleCount: 30,
        spread: 55,
        origin: { y: 0.5 },
        colors: ['#a855f7', '#6366f1', '#f59e0b', '#ec4899'],
        disableForReducedMotion: true,
      });
    }, 400);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const pulseTimer = setInterval(() => {
      setVoidPulse(true);
      setTimeout(() => setVoidPulse(false), 1000);
    }, 3000);

    const shiftTimer = setInterval(() => {
      if (Math.random() < 0.15) {
        setTextShift(true);
        setTimeout(() => setTextShift(false), 200);
      }
    }, 1500);

    // Glow animation
    const glowTimer = setInterval(() => {
      setGlowIntensity(0.3 + Math.random() * 0.4);
    }, 2000);

    return () => {
      clearInterval(pulseTimer);
      clearInterval(shiftTimer);
      clearInterval(glowTimer);
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95">
      <div 
        className={`bg-gradient-to-br from-purple-950 via-black to-indigo-950 border border-purple-500 max-w-md w-full mx-4 relative ${
          void_pulse ? 'animate-pulse shadow-2xl shadow-purple-500/50' : ''
        }`}
        style={{
          boxShadow: `0 0 ${30 + glowIntensity * 40}px rgba(147, 51, 234, ${glowIntensity}), 0 0 ${60 + glowIntensity * 60}px rgba(245, 158, 11, ${glowIntensity * 0.3}), inset 0 0 30px rgba(147, 51, 234, 0.1)`,
          transition: 'box-shadow 1.5s ease-in-out',
        }}
      >
        {/* Cosmic background pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: `radial-gradient(circle at 25% 25%, purple 1px, transparent 1px),
                             radial-gradient(circle at 75% 75%, indigo 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />

        <div className="relative z-10">
          <div className="bg-purple-900/50 text-purple-100 px-3 py-2 flex justify-between items-center">
            <span className={`text-sm font-bold font-serif ${textShift ? 'transform translate-x-0.5' : ''}`}>
              {content.title}
            </span>
            {content.dismissible && (
              <button 
                onClick={onDismiss}
                className="hover:bg-purple-800 px-2 py-0.5 text-xs rounded"
              >
                ◯
              </button>
            )}
          </div>
          
          <div className="p-4 font-serif text-purple-200">
            <div className="border border-purple-400 p-4 bg-black/40 backdrop-blur-sm">
              <p className={`leading-relaxed whitespace-pre-wrap ${textShift ? 'transform translate-x-0.5' : ''}`}>
                {content.message}
              </p>
            </div>
            
            {content.actionButtons && content.actionButtons.length > 0 && (
              <div className="mt-4 flex gap-2">
                {content.actionButtons.map((button, index) => (
                  <button
                    key={index}
                    onClick={() => onAction?.(button.action)}
                    className="border border-purple-400 bg-purple-950/50 text-purple-200 px-4 py-2 hover:bg-purple-800 hover:text-purple-100 transition-all font-serif text-sm backdrop-blur-sm"
                  >
                    {button.text}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
